// Article dynamic blocks — every field consumed from Site Settings via useSiteSettings.
// Zero hardcoded copy or URLs. Zero business logic in components — just presentation.
import React, { useState } from "react";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { toast } from "sonner";
import { trackEvent, trackCtaClick } from "@/lib/ga";

// ── CTA — chooses a topic-specific variant when the article's keywords match ──
// Renders ONE conversion button (Contact Us) with an optional heading and
// body copy. No newsletter, no email input — Newsletter is its own block.
// Maps the article's topic to the closest Contact-form topic option so
// the enquiry arrives pre-qualified. Labels must match webCopy.contactTopics.
const _TOPIC_RULES = [
  ["Manufacturing Services", ["oem", "odm", "private label", "contract manufacturing", "prototype", "shop drawings", "custom furniture manufacturing"]],
  ["Quality Control", ["quality control", "qc", "aql", "golden sample", "inspection service"]],
  ["Factory Inspection", ["factory audit", "factory inspection", "capability assessment"]],
  ["Supplier Verification", ["verification", "due diligence", "scam", "legal entity"]],
  ["Production Management", ["production management", "monitoring", "capacity planning", "multi-factory"]],
  ["Import & Export Support", ["shipping", "logistics", "container", "customs", "freight", "export documentation", "consolidation"]],
  ["Trading Services", ["turnkey", "project supply", "trading structures", "merchant exporter", "trading company"]],
  ["Business Representation", ["business representation", "local presence", "execution partner"]],
  ["Product Sourcing", ["sourcing", "wholesale", "packages", "procurement", "manufacturers", "suppliers", "ff&e"]],
];

function _contactTopicFor(article) {
  const hay = [
    article?.title,
    article?.seoAdvanced?.primaryKeyword,
    ...((article?.seoAdvanced?.secondaryKeywords || []).map((s) => s.keyword)),
  ].filter(Boolean).join(" ").toLowerCase();
  for (const [label, words] of _TOPIC_RULES) {
    if (words.some((w) => hay.includes(w))) return label;
  }
  return "";
}

export function ArticleCta({ article }) {
  const { data: ss } = useSiteSettings();
  if (!ss) return null;
  const variants = ss.ctaVariants || [];
  // Country hubs get a country-specific CTA — variant matching against
  // legacy keywords can otherwise surface an unrelated country name.
  const hubCta = _countryHubCta(article, ss);
  const heading = hubCta?.heading || _pickVariant(article, variants)?.heading || ss.defaultCtaHeading;
  if (!heading) return null;
  const body = hubCta?.body || _pickVariant(article, variants)?.body || ss.defaultCtaBody;
  const contactLabel = ss.contactCtaLabel || "Contact Us";
  // Carry article context into the contact form (topic preselect +
  // source attribution) so enquiries arrive qualified.
  const base = ss.contactUsUrl || "/contact";
  const params = new URLSearchParams();
  const topic = _contactTopicFor(article);
  if (topic) params.set("topic", topic);
  if (article?.slug) params.set("source", `blog/${article.slug}`);
  const qs = params.toString();
  const contact = qs ? `${base}${base.includes("?") ? "&" : "?"}${qs}` : base;
  return (
    <section
      className="my-14 border border-neutral-200 rounded-2xl p-7 md:p-8 bg-neutral-50"
      data-testid="article-cta"
    >
      <h3 className="text-2xl md:text-[28px] font-semibold text-neutral-900 leading-tight tracking-tight">
        {heading}
      </h3>
      {body && (
        <p className="mt-2 text-neutral-700 leading-relaxed">{body}</p>
      )}
      <div className="mt-5">
        <a
          href={contact}
          onClick={() => trackCtaClick(contactLabel, "article-inline", {
            article_slug: article?.slug || "",
            cta_topic: topic || "default",
          })}
          className="inline-flex items-center gap-2 bg-neutral-900 text-white rounded-full px-6 py-3 text-sm font-medium hover:bg-neutral-800"
          data-testid="article-cta-button"
        >
          {contactLabel} <span aria-hidden>→</span>
        </a>
      </div>
    </section>
  );
}

// ── Newsletter — separate compact block, rendered after Related Articles. ──
export function ArticleNewsletter({ article }) {
  const { data: ss } = useSiteSettings();
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle"); // idle | loading | ok | err
  if (!ss) return null;
  const heading = ss.newsletterHeading;
  const body = ss.newsletterBody;
  const buttonLabel = ss.newsletterButtonLabel || "Subscribe";
  if (!heading) return null;

  async function onSubmit(e) {
    e.preventDefault();
    if (!email) return;
    setState("loading");
    try {
      const r = await fetch("/api/seo/newsletter/subscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, source: `blog:${article?.slug || ""}` }),
      });
      if (r.ok) {
        const data = await r.json().catch(() => ({}));
        setState("ok");
        setEmail("");
        trackEvent("newsletter_signup", { source: `blog:${article?.slug || ""}` });
        toast.success(data.already_subscribed ? "You're already subscribed" : "Subscribed");
      }
      else { setState("err"); toast.error("Please try again"); }
    } catch { setState("err"); toast.error("Please try again"); }
  }

  return (
    <section
      className="my-14 border border-neutral-200 rounded-2xl p-7 md:p-8"
      data-testid="article-newsletter"
    >
      <h3 className="text-xl md:text-2xl font-semibold text-neutral-900 leading-tight">
        {heading}
      </h3>
      {body && (
        <p className="mt-2 text-neutral-600 text-sm leading-relaxed">{body}</p>
      )}
      <form
        onSubmit={onSubmit}
        className="mt-4 flex flex-col sm:flex-row gap-2 max-w-lg"
        data-testid="article-newsletter-form"
      >
        <input
          type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
          placeholder="you@company.com"
          className="flex-1 min-w-0 border border-neutral-300 rounded-full px-5 py-3 text-sm focus:outline-none focus:border-neutral-900"
          data-testid="article-newsletter-email"
        />
        <button
          type="submit"
          disabled={state === "loading"}
          className="rounded-full bg-neutral-900 text-white px-6 py-3 text-sm font-medium hover:bg-neutral-800 disabled:opacity-60 whitespace-nowrap"
          data-testid="article-newsletter-submit"
        >
          {state === "loading" ? "…" : buttonLabel}
        </button>
      </form>
    </section>
  );
}

// ── Country hub CTA override — ensures the heading always names the
// current country, never a legacy variant that happens to match on
// generic keywords like "sourcing" or "asia".
const _COUNTRY_LABELS = {
  vietnam: "Vietnam",
  indonesia: "Indonesia",
  thailand: "Thailand",
  malaysia: "Malaysia",
  philippines: "the Philippines",
};

function _countryHubCta(article, ss) {
  // Match either an explicit country hub OR a country-tagged supporting
  // article. This ensures Vietnam/Malaysia/etc. articles never surface
  // a legacy Indonesia CTA due to keyword substring collisions.
  let code = article?.countryHubFor;
  if (!code) {
    const hay = ((article?.slug || "") + " " +
                    ((article?.seoAdvanced || {}).primaryKeyword || "")).toLowerCase();
    for (const c of Object.keys(_COUNTRY_LABELS)) {
      if (hay.includes(c)) { code = c; break; }
    }
  }
  const label = _COUNTRY_LABELS[code];
  if (!label) return null;
  return {
    heading: `Need eyes on the ground in ${label}?`,
    body:
      ss?.defaultCtaBody ||
      `MTS verifies suppliers, audits factories and runs AQL-based inspections at every production milestone in ${label} — reported in writing.`,
  };
}


function _pickVariant(article, variants) {
  if (!Array.isArray(variants) || variants.length === 0) return null;
  // Article's taxonomy (populated via depth=2 in blogApi).
  const artVerticalSlug = _slugOfRelation(article?.vertical);
  const artCategorySlug = _slugOfRelation(article?.category);

  // Pass 1 — exact vertical + category match.
  if (artVerticalSlug && artCategorySlug) {
    for (const v of variants) {
      if (_slugOfRelation(v.vertical) === artVerticalSlug &&
          _slugOfRelation(v.category) === artCategorySlug) return v;
    }
  }
  // Pass 2 — exact vertical only.
  if (artVerticalSlug) {
    for (const v of variants) {
      if (_slugOfRelation(v.vertical) === artVerticalSlug && !v.category) return v;
    }
  }
  // Pass 3 — topic-keyword substring fallback (legacy).
  const hay = [
    article?.title,
    ...(article?.seoAdvanced?.primaryKeyword ? [article.seoAdvanced.primaryKeyword] : []),
    ...((article?.seoAdvanced?.secondaryKeywords || []).map((s) => s.keyword)),
  ].filter(Boolean).join(" ").toLowerCase();
  for (const v of variants) {
    const words = (v.topicKeywords || "").split(",").map((w) => w.trim().toLowerCase()).filter(Boolean);
    if (words.some((w) => hay.includes(w))) return v;
  }
  return null;
}

// Payload relationships come back as either an id string or a
// populated object with `slug`. Extract the slug in both shapes.
function _slugOfRelation(rel) {
  if (!rel) return "";
  if (typeof rel === "string") return "";  // unpopulated id — cannot match
  return (rel.slug || "").toLowerCase();
}

// ── Trust — small checklist ──
// ── Trust — small checklist (retained for potential legacy usage; not
// rendered by the current article layout which uses only ArticleCta +
// ArticleNewsletter. Safe to keep — returns null when trustPoints is empty). ──
export function ArticleTrust() {
  const { data: ss } = useSiteSettings();
  const items = ss?.trustPoints?.filter((t) => t?.text) || [];
  if (items.length === 0) return null;
  return (
    <section className="my-10 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
      {items.map((it, i) => (
        <div key={i} className="flex items-start gap-2 text-neutral-800">
          <span className="text-emerald-700 font-bold leading-6">✓</span>
          <span>{it.text}</span>
        </div>
      ))}
    </section>
  );
}
